import type { ListSocialPostsResponse } from './types.generated';

/** One post as returned by the list endpoint. */
export type SocialPost = ListSocialPostsResponse[number];

/**
 * The cursor-paginated list envelope SKY-4767 introduces. Declared here, ahead
 * of the spec, so the adapter already reads both shapes: whichever deploys
 * first, the list keeps working.
 */
export interface SocialPostsPage {
  items: SocialPost[];
  /** Cursor of the next page, `null` on the last one. */
  nextCursor: string | null;
}

/** Every shape the list endpoint may answer with: today's array or the SKY-4767 page. */
export type SocialPostsListResponse = ListSocialPostsResponse | SocialPostsPage;

const EMPTY_POSTS: readonly SocialPost[] = [];

/**
 * Extracts the posts from a `listSocialPosts` response.
 *
 * The response is a plain array today; SKY-4767 changes it to
 * `{ items, nextCursor }`. Everything that reads the list goes through this
 * selector (and {@link useSocialPosts}), so that change stays local to this file.
 *
 * @param response - The raw `listSocialPosts` response, `undefined` while loading.
 * @returns The posts, or a stable empty array when there is no data (yet).
 */
export const selectSocialPosts = (
  response: SocialPostsListResponse | null | undefined,
): readonly SocialPost[] => {
  // `null` too: an empty list may come back as a `null` body.
  if (response === undefined || response === null) return EMPTY_POSTS;
  return Array.isArray(response) ? response : response.items;
};

/**
 * Cursor of the next page of a `listSocialPosts` response: always `null` for
 * today's unpaginated array, the page's `nextCursor` once SKY-4767 lands.
 *
 * @param response - The raw `listSocialPosts` response, `undefined` while loading.
 */
export const selectSocialPostsNextCursor = (
  response: SocialPostsListResponse | null | undefined,
): string | null =>
  response === undefined || response === null || Array.isArray(response)
    ? null
    : response.nextCursor;
